import { subscribeDevice, unsubscribeDevice } from "@/api/devices";
import { usePushNotificationStore } from "@/stores/pushNotification.store";

const TOKEN_STORAGE_KEY = "fcm_token";
const CHANNEL_ID = "HUNGRYHUB_PARTNER";
const CHANNEL_NAME = "HUNGRYHUB PARTNER";
const CHANNEL_IMPORTANCE = 4;
const CHANNEL_SOUND = "hungry";

const DEEP_LINK_MAP = {
  booking: (d) => `/booking?bookingId=${d.id ?? ""}`,
  review: () => "/review-management",
  allotment: () => "/allotment",
  package: () => "/package",
  newsMarketing: () => "/news",
  block_request: () => "/allotment",
};

function resolveDeepLink(data) {
  if (!data) return "/notifications";
  if (data.deepLink) return data.deepLink;
  const builder = DEEP_LINK_MAP[data.type];
  return builder ? builder(data) : "/notifications";
}

async function getPlatform() {
  const { Capacitor } = await import("@capacitor/core");
  return Capacitor.getPlatform();
}

async function loadPushModule() {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  return PushNotifications;
}

function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (_) {
    return null;
  }
}

function logSubscribeError(e) {
  console.warn("[FCM] subscribe on refresh failed", e?.response?.status || e?.message);
}

function storeToken(token) {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch (_) {
    /* storage may be unavailable in private mode */
  }
}

function clearStoredToken() {
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (_) {
    /* storage may be unavailable in private mode */
  }
}

export function usePushNotifications() {
  const pushStore = usePushNotificationStore();
  let listenersAttached = false;

  async function openAppSettings() {
    try {
      const platform = await getPlatform();
      if (platform === "ios") {
        window.location.href = "app-settings:";
        return;
      }
      if (platform === "android") {
        const { registerPlugin } = await import("@capacitor/core");
        const plugin = registerPlugin("NotificationPermission");
        await plugin.openNotificationSettings();
        return;
      }
    } catch (e) {
      console.warn("[FCM] openAppSettings failed", e);
    }
  }

  async function attachListeners() {
    if (listenersAttached) return;
    const platform = await getPlatform();
    console.log("[FCM] attaching listeners, platform:", platform);
    if (platform !== "ios" && platform !== "android") {
      console.log("[FCM] non-native platform, skipping push init");
      return;
    }

    const PushNotifications = await loadPushModule();

    if (platform === "android") {
      try {
        await PushNotifications.createChannel({
          id: CHANNEL_ID,
          name: CHANNEL_NAME,
          importance: CHANNEL_IMPORTANCE,
          sound: CHANNEL_SOUND,
        });
        console.log("[FCM] Android channel created:", CHANNEL_ID);
      } catch (e) {
        console.warn("[FCM] createChannel failed", e);
      }
    }

    await PushNotifications.addListener("registration", async (token) => {
      const value = token.value;
      console.log("[FCM_TOKEN] >>>", value, "<<<");
      console.log("[FCM_TOKEN_COPY]", value);
      storeToken(value);
      window.__fcmToken = value;
      // If user is already logged in, subscribe immediately
      try {
        const { useUserStore } = await import("@/stores/user.store");
        const userStore = useUserStore();
        if (userStore.token) {
          console.log("[FCM] token refreshed while logged in, re-subscribing");
          await subscribeDevice(value).catch((e) => logSubscribeError(e));
        }
      } catch (e) {
        console.warn("[FCM] post-token subscribe check failed", e);
      }
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.error("[FCM_ERROR]", JSON.stringify(err));
    });

    await PushNotifications.addListener("pushNotificationReceived", (notification) => {
      console.log("[FCM_RECEIVED]", JSON.stringify(notification.notification));
      pushStore.setForegroundNotification(notification.notification);
    });

    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      console.log("[FCM_ACTION]", JSON.stringify(action));
      const data = action.notification?.data || {};
      pushStore.setPendingDeepLink(resolveDeepLink(data));
    });

    listenersAttached = true;
    console.log("[FCM] listeners attached");
  }

  async function initPush() {
    try {
      console.log("[FCM] initPush start");
      await attachListeners();
      const platform = await getPlatform();
      if (platform !== "ios" && platform !== "android") {
        console.log("[FCM] not native, init aborted");
        return false;
      }

      const PushNotifications = await loadPushModule();
      let perm = await PushNotifications.checkPermissions();
      console.log("[FCM] initial permission:", perm.receive);
      if (perm.receive === "prompt") {
        perm = await PushNotifications.requestPermissions();
        console.log("[FCM] requested permission:", perm.receive);
      }
      if (perm.receive !== "granted") {
        console.warn("[FCM] permission not granted:", perm.receive);
        if (perm.receive === "denied") {
          pushStore.setPermissionDenied(true);
        }
        return false;
      }
      pushStore.setPermissionDenied(false);
      console.log("[FCM] permission granted, calling register()");
      // On Android, register() rejects with "then() is not implemented" but the
      // token is still delivered via the 'registration' event listener. Fire-and-forget.
      try {
        await PushNotifications.register();
        console.log("[FCM] register() resolved");
      } catch (e) {
        console.warn("[FCM] register() rejected (non-fatal on Android):", e?.message);
      }
      console.log("[FCM] awaiting token event...");
      return true;
    } catch (e) {
      console.warn("[FCM] initPush failed", e);
      return false;
    }
  }

  async function checkAndRequestPermission() {
    const platform = await getPlatform();
    if (platform !== "ios" && platform !== "android") return "unsupported";
    const PushNotifications = await loadPushModule();
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === "granted") {
      pushStore.setPermissionDenied(false);
      try {
        await PushNotifications.register();
      } catch (e) {
        console.warn("[FCM] register() rejected", e?.message);
      }
    } else if (perm.receive === "denied") {
      pushStore.setPermissionDenied(true);
    }
    return perm.receive;
  }

  async function getPermissionStatus() {
    try {
      const platform = await getPlatform();
      if (platform !== "ios" && platform !== "android") return "unsupported";
      const PushNotifications = await loadPushModule();
      const perm = await PushNotifications.checkPermissions();
      return perm.receive;
    } catch (e) {
      console.warn("[FCM] getPermissionStatus failed", e);
      return "unknown";
    }
  }

  async function registerPluginPushIfGranted() {
    try {
      const PushNotifications = await loadPushModule();
      await PushNotifications.register();
    } catch (e) {
      console.warn("[FCM] register rejected (non-fatal on Android):", e?.message);
    }
  }

  async function syncOnAppResume() {
    console.log("[FCM] syncOnAppResume");
    // Re-init push: handles permission re-grant (user toggled in system
    // settings) and triggers the registration event. The registration
    // listener auto-subscribes if the user is already logged in.
    await initPush();
    // Explicitly try to subscribe with whatever token is already in
    // storage, in case the backend lost it (e.g., user logged in on
    // another device, or backend cleaned up stale records).
    try {
      const { useUserStore } = await import("@/stores/user.store");
      const userStore = useUserStore();
      if (userStore.token) {
        await subscribeDeviceIfNeeded();
      }
    } catch (e) {
      console.warn("[FCM] resume subscribe failed", e);
    }
  }

  async function subscribeDeviceIfNeeded() {
    const token = getStoredToken();
    if (!token) {
      console.log("[FCM] subscribeDeviceIfNeeded: no token in storage yet");
      return;
    }
    try {
      console.log("[FCM] POST dashboard/staff/subscribe");
      const res = await subscribeDevice(token);
      console.log("[FCM] subscribe OK:", res?.status, res?.data);
    } catch (e) {
      console.warn("[FCM] subscribe failed", e?.response?.status, e?.response?.data || e?.message);
    }
  }

  async function disposePush() {
    const token = getStoredToken();
    if (token) {
      try {
        console.log("[FCM] POST /api/dashboard/staff/unsubscribe");
        const res = await unsubscribeDevice(token);
        console.log("[FCM] unsubscribe OK:", res?.status);
      } catch (e) {
        console.warn(
          "[FCM] unsubscribe failed",
          e?.response?.status,
          e?.response?.data || e?.message,
        );
      }
    }
    clearStoredToken();
    try {
      const PushNotifications = await loadPushModule();
      await PushNotifications.removeAllListeners();
    } catch (e) {
      console.warn("[FCM] removeAllListeners failed", e);
    }
    listenersAttached = false;
  }

  return {
    initPush,
    subscribeDeviceIfNeeded,
    disposePush,
    openAppSettings,
    checkAndRequestPermission,
    getPermissionStatus,
    registerPluginPushIfGranted,
    syncOnAppResume,
  };
}
